import {
    loadGptScript,
    ensureGptCmd,
    adBlockEnabled,
} from 'component-library/advertising/gpt/GptAdProvider/utils'
import debounce from 'lodash.debounce'
import { getGptEnabled } from 'component-library/advertising/gpt/GptAdProvider/GptAdProvider'
import { getGrapeshotChannels } from 'component-library/advertising/grapeshot'
import { Logger } from 'typescript-log'
import { isServerEnvironment } from 'common/environment'
import { PromiseCompletionSource } from 'project-watchtower/dist/cjs/runtime/universal'
import { SizeMapping } from 'common/advertising/size-mapping'
import { GptAdSlotDefinition } from 'common/advertising/gpt-ad-slot-definintion'

// defined in @types/doubleclick-gpt
export type GoogleTag = typeof googletag
declare const moatPrebidApi:
    | {
          slotDataAvailable(): boolean
          setMoatTargetingForSlot(slot: googletag.Slot): void
          safetyDataAvailable(): boolean
      }
    | undefined

export interface SlotRenderEndedEvent extends googletag.events.SlotRenderEndedEvent {
    unit: GptAdSlotDefinition
}

interface SlotMap {
    [id: string]: {
        gptSlot: googletag.Slot
        definition: GptAdSlotDefinition
    }
}

interface MediaQueryMap {
    [width: number]: {
        mq: MediaQueryList
        slotIds: string[]
    }
}

export type SlotRenderEndedEventHandler = (event: SlotRenderEndedEvent) => void

interface GptManagerOptions {
    advertisingConsentGiven: boolean
    enableGrapeshot?: boolean
    enableMoatYieldIntelligence?: boolean
    logger: Logger
}

export class GptManager {
    private initialised = false
    private displayedSlots: SlotMap = {}
    private mediaQueries: MediaQueryMap = {}
    private gpt: typeof googletag
    private apiAndPubAdsReady: Promise<void>
    private active = !adBlockEnabled() && getGptEnabled()
    private enableGrapeshot = false
    private enableMoatYieldIntelligence = false
    private advertisingConsentGiven = false
    private logger: Logger
    private moatYieldIntelligenceInterval: any = null
    private moatYieldIntelligenceReady: boolean = false
    private MOAT_YI_RETRY_INTERVAL = 250

    private refreshAllSlots = debounce(() => {
        const slotIds = Object.keys(this.displayedSlots)
        const slots = slotIds.map(id => this.displayedSlots[id].gptSlot)
        this.clearSlots(slots)
        window.requestIdleCallback(() => this.refreshSlots(slotIds, slots), { timeout: 300 })
    }, 250)

    constructor(options: GptManagerOptions) {
        this.logger = options.logger
        this.gpt = ensureGptCmd()
        this.setOptions(options)

        // Polls the GPT API and PubAds ready status
        const promiseSource = new PromiseCompletionSource<void>()
        this.apiAndPubAdsReady = promiseSource.promise
        this.addToGptQueue(async () => {
            do {
                const isReady = this.gpt.apiReady && this.gpt.pubadsReady
                if (isReady) {
                    break
                }

                await new Promise(resolve => setTimeout(resolve, 100))
                // eslint-disable-next-line no-constant-condition
            } while (true)

            /**
             * At this point:
             * - gpt.js has loaded and initialised
             * - gpt.js API methods are ready to be called
             * - gpt.js PubAds service methods are ready to be called
             */
            promiseSource.resolve(undefined)
        })

        if (!isServerEnvironment()) {
            this.addToGptQueue(() => {
                const win = (window as unknown) as Window & { googletag: typeof googletag }
                this.gpt = win.googletag
            })
        }
    }

    // Set the options for GPT manager can be updated on the fly in the case of A/B testing for grapeshot if it happens
    // Logger not reset
    public setOptions(options: GptManagerOptions) {
        this.enableGrapeshot = options.enableGrapeshot || false
        this.enableMoatYieldIntelligence = options.enableMoatYieldIntelligence || false
        this.advertisingConsentGiven = options.advertisingConsentGiven
    }

    companionAdsEnabled() {
        if (typeof window !== 'undefined') {
            const href = window.location.href
            return href.indexOf('companionAds=true') > 0
        }
        return false
    }

    // This should be called right before a pubads().refresh(...)
    setMoatTargetingOnSlots(gptSlots: googletag.Slot[]) {
        // Moat YI is enabled and ready to be used - both page/slot data
        if (this.moatYieldIntelligenceReady && moatPrebidApi) {
            for (let i = 0; i < gptSlots.length; i++) {
                moatPrebidApi.setMoatTargetingForSlot(gptSlots[i])
            }
        }
    }

    /**
     * changeCorrelator: boolean - changeCorrelator specifies whether or not a new correlator is to be generated for fetching ads
     * Google's servers maintain the Correlator (~30 seconds) so this isn't guaranteed depending on timing
     * https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_refresh
     */
    async displaySlots(slots: GptAdSlotDefinition[], changeCorrelator = false) {
        await this.apiAndPubAdsReady

        const additionalTargetingWork: Array<Promise<any>> = []

        if (this.enableGrapeshot) {
            additionalTargetingWork.push(this.setGrapeshotTargeting())
        }

        if (this.enableMoatYieldIntelligence) {
            additionalTargetingWork.push(this.waitUntilMoatYieldIntelligenceIsLoaded())
        }

        await Promise.all(additionalTargetingWork)

        const gptSlots = slots
            .map(slot => this.defineGptSlot(slot))
            .filter((slot): slot is googletag.Slot => !!slot)

        this.setMoatTargetingOnSlots(gptSlots)

        this.gpt.pubads().refresh(gptSlots, { changeCorrelator })
    }

    async setGrapeshotTargeting() {
        const categories = await getGrapeshotChannels(this.logger, 500)
        this.logger.info(
            { categories },
            'Setting gs_cat for gpt pubads() using grapeshot categories'
        )

        // page level targeting across all slots
        this.gpt.pubads().setTargeting('gs_cat', categories)
    }

    async waitUntilMoatYieldIntelligenceIsLoaded() {
        return new Promise(resolve => {
            let count = 0

            // Clear if interval is set already
            if (this.moatYieldIntelligenceInterval) {
                clearInterval(this.moatYieldIntelligenceInterval)
            }

            const start = Date.now()
            this.moatYieldIntelligenceInterval = setInterval(() => {
                // Give up after a certain number of retries as we need to load ads,
                // and this is being awaited as additional work
                count += this.MOAT_YI_RETRY_INTERVAL

                if (
                    typeof moatPrebidApi !== 'undefined' &&
                    typeof moatPrebidApi.slotDataAvailable === 'function' &&
                    typeof moatPrebidApi.safetyDataAvailable === 'function' &&
                    moatPrebidApi.safetyDataAvailable() &&
                    moatPrebidApi.slotDataAvailable()
                ) {
                    clearInterval(this.moatYieldIntelligenceInterval)
                    this.logger.debug(`Moat ready to be used, took=${Date.now() - start}ms`)
                    this.moatYieldIntelligenceReady = true
                    resolve(true)
                }

                // Lets retry until a second has elapsed
                if (count >= 1000) {
                    clearInterval(this.moatYieldIntelligenceInterval)
                    this.logger.debug(
                        'Timeout exceeded waiting for moat to initialize or no slot data is available'
                    )
                    resolve(false)
                }
            }, this.MOAT_YI_RETRY_INTERVAL)
        })
    }

    async destroySlots(slots: GptAdSlotDefinition[]) {
        await this.apiAndPubAdsReady

        const slotsToDestroy = slots
            .map(slot => {
                const displayedSlot = this.displayedSlots[slot.id]
                if (!displayedSlot) {
                    return undefined
                }

                const gptSlot = displayedSlot.gptSlot
                delete this.displayedSlots[slot.id]

                if (slot.sizeMapping) {
                    this.removeMediaQuery(slot.id, slot.sizeMapping)
                }

                return gptSlot
            })
            .filter((slot): slot is googletag.Slot => slot !== undefined)

        if (!slotsToDestroy.length) {
            return
        }

        this.gpt.destroySlots(slotsToDestroy)
    }

    async registerOnSlotRenderEnded(handler: SlotRenderEndedEventHandler) {
        await this.apiAndPubAdsReady

        this.gpt.pubads().addEventListener('slotRenderEnded', event => {
            handler(event as SlotRenderEndedEvent)
        })
    }

    /**
     * Idempotent function to ensure GPT library is loaded and initialised.
     * This does NOT guarantee that the API and PubAds service are ready!
     */
    async ensureInitialised() {
        if (this.initialised) {
            return
        }

        await loadGptScript()

        await new Promise(resolve => {
            this.addToGptQueue(() => {
                const pubAds = this.gpt.pubads()

                // Can be undefined with ad blockers
                if (!pubAds) {
                    return
                }

                if (!this.advertisingConsentGiven) {
                    pubAds.setRequestNonPersonalizedAds(1)
                }

                if (this.companionAdsEnabled()) {
                    this.logger.info('pubAds.enableVideoAds()=enabled')
                    pubAds.enableVideoAds()
                }
                pubAds.setForceSafeFrame(false)
                pubAds.enableSingleRequest()

                // disableInitialLoad 'should' be off when autoplay is not enabled // TODO 7NEWS
                pubAds.disableInitialLoad()

                this.gpt.enableServices()

                /**
                 * At this point:
                 * - gpt.js has loaded and initialised
                 * - gpt.js API is not necessarily ready yet
                 * - gpt.js PubAds service is not necessarily ready yet
                 */
                resolve()
            })
        })

        this.initialised = true
    }

    /**
     * Don't use this! This should only be used by initialisation functions.
     * Use `await this.apiAndPubAdsReady` to ensure the API and PubAds service are loaded
     * before invoking GPT methods.
     */
    private addToGptQueue(fn: () => void) {
        this.gpt.cmd.push(fn)
    }

    private async clearSlots(slots?: googletag.Slot[]) {
        await this.apiAndPubAdsReady

        this.gpt.pubads().clear(slots)
        this.cleanupBonzaiSkins()
    }

    private tryDefineSlot(slot: GptAdSlotDefinition, isRetry: boolean): googletag.Slot | null {
        let gptSlot

        if (slot.outOfPage) {
            gptSlot = this.gpt.defineOutOfPageSlot(slot.adUnitPath, slot.id)
        } else {
            // The value below is used when there are no size mappings, or the viewport width is below all specified size mappings.
            // We always cover all breakpoints in our size mappings so this fallback should not be used
            // https://developers.google.com/doubleclick-gpt/reference#googletag.defineSlot
            gptSlot = this.gpt.defineSlot(slot.adUnitPath, [0, 0], slot.id)
        }

        // Don't retry when adUnitPath is empty, it will fail
        if (gptSlot || isRetry || slot.adUnitPath === '') {
            return gptSlot
        }

        /**
         * At this point, Googletag has returned `null`, which means a slot with
         * the same element id still exists in Googletag's internal slot list.
         */
        this.logger.warn({ slot }, 'GPT slot could not be defined, retrying! (duplicate id?)')
        const slotToDestroy = Object.keys(this.displayedSlots)
            .filter(id => id === slot.id)
            .map(id => this.displayedSlots[id].gptSlot)
        this.gpt.destroySlots(slotToDestroy)
        return this.tryDefineSlot.call(this, slot, true)
    }

    private defineGptSlot(slot: GptAdSlotDefinition): googletag.Slot | null {
        if (!this.active) {
            return null
        }

        const gptSlot = this.tryDefineSlot(slot, false)

        if (!gptSlot) {
            // Static pages do not have ad unit paths
            if (slot.adUnitPath !== '') {
                this.logger.error({ slot }, 'GPT slot could not be defined! (duplicate id?)')
            }
            return gptSlot
        }

        /**
         * Handle ad exclusion labels: https://support.google.com/dfp_premium/answer/3238504?hl=en
         * This is required for scenarios such as: a slot that only serves a specific campaign that
         * should NOT have programmatic/house/paid ads so that it can auto-collapse when the campaign
         * is not running.
         */
        const { exclusionLabels = [] } = slot
        for (const label of exclusionLabels) {
            gptSlot.setCategoryExclusion(label)
        }

        const targeting = slot.targeting

        if (targeting) {
            Object.keys(targeting).forEach(key => {
                gptSlot.setTargeting(key, targeting[key])
            })
        }

        if (slot.sizeMapping) {
            // Tests don't have clientWidth, so default to innerWidth
            const clientWidth = document.documentElement.clientWidth || window.innerWidth
            const scrollbarWidth = window.innerWidth - clientWidth
            let smbuilder = this.gpt.sizeMapping()
            slot.sizeMapping.forEach(mapping => {
                // It appears that GPT works on width excluding scrollbars, but
                // breakpoints include scrollbars. This takes this into account
                let width = mapping.viewport[0] - scrollbarWidth
                if (width < 0) {
                    width = 0
                }
                const height = mapping.viewport[1]
                smbuilder = smbuilder.addSize([width, height], mapping.slot)
            })
            const sizeMapping = smbuilder.build()
            gptSlot.defineSizeMapping(sizeMapping)

            this.addMediaQuery(slot.id, slot.sizeMapping)
        }

        if (slot.forceSafeFrame !== undefined) {
            // It appears that some ad blockers you still get an ad slot object,
            // but it does not have a `setForceSafeFrame` function on it
            if (gptSlot.setForceSafeFrame) {
                gptSlot.setForceSafeFrame(slot.forceSafeFrame)
            }
        }

        if (this.companionAdsEnabled()) {
            this.logger.info('gptSlot.addService(this.gpt.companionAds())')
            gptSlot.addService(this.gpt.companionAds())
        }
        gptSlot.addService(this.gpt.pubads())

        // this does not actually display the slot as we set disableInitialLoad()
        // we have to call refresh() to actually display it
        this.gpt.display(slot.id)

        this.displayedSlots[slot.id] = {
            gptSlot,
            definition: slot,
        }

        return gptSlot
    }

    private addMediaQuery(slotId: string, sizeMapping: SizeMapping[]) {
        if (typeof window === 'undefined' || window.matchMedia === undefined) {
            return
        }

        sizeMapping.forEach(mapping => {
            const width = mapping.viewport[0]
            if (width === 0) {
                return
            }

            const existingContainer = this.mediaQueries[width]

            if (existingContainer) {
                if (existingContainer.slotIds.indexOf(slotId) === -1) {
                    existingContainer.slotIds.push(slotId)
                }
                return
            }

            const mqContainer = {
                mq: window.matchMedia(`(min-width: ${width}px)`),
                slotIds: [slotId],
            }
            mqContainer.mq.addListener(this.refreshAllSlots)

            this.mediaQueries[width] = mqContainer
        })
    }

    private removeMediaQuery(slotId: string, sizeMapping: SizeMapping[]) {
        if (typeof window === 'undefined' || window.matchMedia === undefined) {
            return
        }

        sizeMapping.forEach(mapping => {
            const width = mapping.viewport[0]
            const mqContainer = this.mediaQueries[width]

            if (width === 0 || !mqContainer) {
                return
            }

            const index = mqContainer.slotIds.indexOf(slotId)
            if (index !== -1) {
                mqContainer.slotIds.splice(index, 1)
            }

            if (mqContainer.slotIds.length > 0) {
                return
            }

            mqContainer.mq.removeListener(this.refreshAllSlots)
            delete this.mediaQueries[width]
        })
    }

    private async refreshSlots(_slotIds: string[], slots?: googletag.Slot[]) {
        await this.apiAndPubAdsReady

        if (slots) {
            this.setMoatTargetingOnSlots(slots)
        }

        this.gpt.pubads().refresh(slots, { changeCorrelator: false })
    }

    /**
     * SWM-5394
     * Bonzai skins have started to hang around intermittently when resizing the viewport (02/06/18)
     * This function finds the injected Bonzai iframes and removes them. This kills the ad.
     */
    private cleanupBonzaiSkins() {
        const outOfPageSlotIds = Object.keys(this.displayedSlots).filter(slotId => {
            const slot = this.displayedSlots[slotId]
            return slot.definition.outOfPage
        })

        for (const slotId of outOfPageSlotIds) {
            for (const width of Object.keys(this.mediaQueries)) {
                const mq = this.mediaQueries[Number(width)]
                const matches = mq.slotIds.filter(id => id === slotId)
                if (!matches.length) {
                    continue
                }

                if (mq.mq.matches) {
                    continue
                }

                // destroy skins if they are not within the breakpoint range
                const sideSkins = document.querySelectorAll(
                    'iframe[pagetype=leftsideskin], iframe[pagetype=rightsideskin]'
                )
                for (const element of Array.from(sideSkins as any) as HTMLIFrameElement[]) {
                    // IE doesn't have Element.remove()
                    element.parentNode!.removeChild(element)
                }
            }
        }
    }
}

let gptManagerInstance: GptManager | null = null

// Singleton
export const gptManager = (options: GptManagerOptions) => {
    if (gptManagerInstance === null) {
        gptManagerInstance = new GptManager(options)
    } else {
        gptManagerInstance.setOptions(options)
    }
    return gptManagerInstance
}
